/**
 * Flattens AD employee data + user-managed profile data into searchable
 * documents for Fuse.js. Kept pure (no React, no localStorage) so it can be
 * unit-tested; useSearch owns fetching profiles and building the index.
 */

/** Fuse.js keys with weights: identity fields outrank content fields. */
export const SEARCH_KEYS = [
  { name: 'name', weight: 3 },
  { name: 'title', weight: 2 },
  { name: 'department', weight: 1.5 },
  { name: 'office', weight: 1.5 },
  { name: 'emailLocal', weight: 1.5 },
  { name: 'skills', weight: 1 },
  { name: 'expertise', weight: 1 },
  { name: 'software', weight: 1 },
  { name: 'certifications', weight: 1 },
  { name: 'statesLicensed', weight: 1 },
  { name: 'projects', weight: 1 },
  { name: 'bio', weight: 0.5 },
  { name: 'about', weight: 0.5 },
];

/**
 * @param {object[]} employees
 * @param {Record<string, object>} profiles — keyed by employee id
 * @returns {object[]} one doc per employee; `doc.employee` is the original object
 */
export function buildSearchDocs(employees, profiles) {
  return employees.map((employee) => {
    const profile = profiles[employee.id] || {};
    return {
      employee,
      name: employee.displayName || '',
      title: employee.jobTitle || '',
      department: employee.department || '',
      office: employee.officeLocation || '',
      // Local part only — full-email matching breaks the domain-split rule
      emailLocal: (employee.mail || '').split('@')[0],
      skills: [...(profile.skills || []), ...(employee.skills || [])],
      expertise: profile.areasOfExpertise || [],
      software: profile.programsExpertise || [],
      certifications: (profile.certifications || []).flatMap((c) =>
        [c?.name, c?.issuer].filter(Boolean)
      ),
      statesLicensed: profile.statesLicensed || [],
      projects: [
        ...(profile.moregroupProjects || []).map((p) => p?.name || p?.role),
        ...(employee.openAssetProjects || []).map((p) =>
          typeof p === 'string' ? p : p?.name
        ),
      ].filter(Boolean),
      bio: [profile.bio, employee.bio].filter(Boolean),
      about: [
        profile.coreExpertise,
        profile.askMeAbout,
        profile.practiceArea,
        profile.highSchoolAlmaMater,
        profile.collegeAlmaMater,
        profile.volunteerAt,
        profile.priorProjects,
        profile.innovationTopics,
        profile.mentorTopics,
        ...(profile.interests || []),
      ].filter(Boolean),
    };
  });
}

/**
 * Case-insensitive substring match over identity fields only (name, title,
 * department, office, email local-part) — the fallback for 1-char queries,
 * where Fuse's minMatchCharLength (2) would otherwise return nothing and
 * empty the whole directory. Empty query returns the list unchanged.
 */
export function filterEmployeesBySubstring(employees, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return employees;
  return employees.filter((employee) => {
    const emailLocal = (employee.mail || '').split('@')[0];
    return [
      employee.displayName,
      employee.jobTitle,
      employee.department,
      employee.officeLocation,
      emailLocal,
    ].some((field) => (field || '').toLowerCase().includes(q));
  });
}
